import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  LogOut,
  ShieldCheck,
  UserRound,
  LayoutDashboard,
  Building2,
  HardHat,
  Leaf,
  Ship,
} from "lucide-react";

const LOGO_ONE = "/assets/images/logo1.png";
const LOGO_TWO = "/assets/images/logo2.png";

const portalConfig = {
  registry: {
    title: "Registry Portal",
    subtitle: "Enterprise registration, licence records and corporate verification",
    label: "OGFZA Digital Registry",
    route: "/portal/registry-portal",
    login: "/portal/login?service=registry",
    icon: Building2,
  },
  engineering: {
    title: "Engineering Portal",
    subtitle: "Development applications, inspections and technical submissions",
    label: "OGFZA Digital Engineering",
    route: "/portal/engineering-portal",
    login: "/portal/login?service=engineering",
    icon: HardHat,
  },
  hse: {
    title: "HSE Portal",
    subtitle: "HSE submissions, inspection requests and incident reporting",
    label: "OGFZA Digital HSE",
    route: "/portal/hse-portal",
    login: "/portal/login?service=hse",
    icon: Leaf,
  },
  operations: {
    title: "Operations Portal",
    subtitle: "Cargo movement, goods transfer and operational approvals",
    label: "OGFZA Digital Operations",
    route: "/portal/operations-portal",
    login: "/portal/login?service=operations",
    icon: Ship,
  },
};

const getPortalKeyFromPath = (pathname) => {
  if (pathname.includes("engineering")) return "engineering";
  if (pathname.includes("hse")) return "hse";
  if (pathname.includes("operations")) return "operations";
  return "registry";
};

const ApplicationHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const savedService = localStorage.getItem("ogfzaPortalService");
  const portalKey =
    portalConfig[savedService] ? savedService : getPortalKeyFromPath(location.pathname);

  const activePortal = portalConfig[portalKey];
  const ActiveIcon = activePortal.icon;

  const userEmail = localStorage.getItem("ogfzaPortalUser") || "Applicant";

  const handleLogout = () => {
    localStorage.removeItem("ogfzaPortalLoggedIn");
    localStorage.removeItem("ogfzaPortalUser");
    localStorage.removeItem("ogfzaPortalService");

    navigate(activePortal.login);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm p-1.5">
              <img
                src={LOGO_ONE}
                alt="OGFZA Logo One"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm p-1.5">
              <img
                src={LOGO_TWO}
                alt="OGFZA Logo Two"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <div>
            <p className="text-[11px] md:text-xs uppercase tracking-[0.18em] text-[#026b07] font-black">
              {activePortal.label}
            </p>

            <h1 className="text-sm md:text-lg font-black text-[#201182] leading-tight">
              {activePortal.title}
            </h1>

            <p className="text-xs text-slate-500 mt-1">
              {activePortal.subtitle}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="hidden md:flex items-center gap-2 rounded-full bg-[#201182]/5 border border-[#201182]/10 px-4 py-2 text-xs font-semibold text-[#201182]">
            <ShieldCheck size={15} />
            Secure Portal Session
          </div>

          <div className="flex items-center gap-2 rounded-full bg-[#026b07]/5 border border-[#026b07]/10 px-4 py-2 text-xs font-semibold text-[#026b07]">
            <ActiveIcon size={15} />
            {activePortal.title}
          </div>

          <div className="flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600">
            <UserRound size={15} className="text-[#026b07]" />
            <span className="max-w-[180px] truncate">{userEmail}</span>
          </div>

          <button
            type="button"
            onClick={() => navigate(activePortal.route)}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-xs md:text-sm font-bold text-slate-700 hover:border-[#201182] hover:text-[#201182] transition-all"
          >
            <LayoutDashboard size={16} />
            Dashboard
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#201182] px-5 py-2.5 text-xs md:text-sm font-bold text-white hover:bg-[#026b07] transition-all"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </div>
    </header>
  );
};

export default ApplicationHeader;